import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  CircularProgress,
  InputAdornment,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Popover,
  Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import FlutterDashIcon from "@mui/icons-material/FlutterDash";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";

import { trpc } from "../lib/trpc";
import { Roles } from "../config/constants";
import { useAuthStore } from "@repo/api-client";

// List of available apps from system config
const AVAILABLE_APPS = [
  "donation",
  "register",
  "new_member",
  "events",
  "analytics",
  "all",
];

// Define typing for editable user object
interface UserFormState {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  enabled: boolean;
  roles: string[];
  apps: string[];
  ChurchID: string;
  StructID: string;
  WFLStructID: string;
  TeacherID: string;
  StrucName?: string;
  WFLStrucName?: string;
  password?: string;
  nickname?: string;
  MemID?: string;
}

const AdminUsers = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { user, login } = useAuthStore();

  // Search and view states
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");

  const [selectedUser, setSelectedUser] = useState<UserFormState | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Determine role permissions of currently logged in user
  const currentUserRoles: string[] = useMemo(() => {
    if (!user?.roles) return [];
    if (Array.isArray(user.roles)) return user.roles;
    if (typeof user.roles === "object") return Object.keys(user.roles);
    return [];
  }, [user?.roles]);

  // "only role that contain ADMIN could edit and add ROLE"
  const canManageRoles = useMemo(() => {
    return currentUserRoles.some((r) => r.toUpperCase().includes("ADMIN"));
  }, [currentUserRoles]);

  // "only ROLE_ADMIN and ROLE_SUPERADMIN could add ADMIN role"
  const isSuperOrAdmin = useMemo(() => {
    return currentUserRoles.some(
      (r) => r === "ROLE_ADMIN" || r === "ROLE_SUPERADMIN",
    );
  }, [currentUserRoles]);

  // Determine if current user can assign WFL admin roles
  const canAssignWflAdmin = useMemo(() => {
    return currentUserRoles.some(
      (r) =>
        r === "ROLE_ADMIN" ||
        r === "ROLE_SUPERADMIN" ||
        r === "ROLE_ADMIN_WFL" ||
        r === "ROLE_WFL_ADMIN",
    );
  }, [currentUserRoles]);

  // Allowed role options for assignment
  const roleOptions = useMemo(() => {
    const allRoles = Object.values(Roles);
    if (isSuperOrAdmin) {
      return allRoles;
    }
    if (canAssignWflAdmin) {
      // WFL admins can assign WFL admin roles as well as non-ADMIN roles
      return allRoles.filter(
        (r) =>
          !r.toUpperCase().includes("ADMIN") ||
          r === Roles.ROLE_ADMIN_WFL ||
          r === Roles.ROLE_WFL_ADMIN,
      );
    }
    // Non-superadmin/admin cannot add roles containing ADMIN
    return allRoles.filter((r) => !r.toUpperCase().includes("ADMIN"));
  }, [isSuperOrAdmin, canAssignWflAdmin]);

  // Delete dialog states
  const [userToDelete, setUserToDelete] = useState<any | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Impersonate states
  const [userToImpersonate, setUserToImpersonate] = useState<any | null>(null);
  const [isImpersonateDialogOpen, setIsImpersonateDialogOpen] = useState(false);

  // Member Search and Selector states
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const { data: memberOptions, isLoading: isMemberLoading } =
    trpc.member.search.useQuery(memberSearchQuery, {
      enabled: memberSearchQuery.trim().length >= 2,
    });

  const selectedMemberOption = useMemo(() => {
    if (!selectedUser?.MemID) return null;
    const memId = Number(selectedUser.MemID);
    const found = memberOptions?.find((m) => m.MemID === memId);
    if (found) return found;
    return {
      MemID: memId,
      Name: selectedUser.first_name || "",
      Surname: selectedUser.last_name || "",
      Nickname:
        selectedUser.nickname !== "not link" ? selectedUser.nickname : "",
    } as any;
  }, [
    selectedUser?.MemID,
    selectedUser?.first_name,
    selectedUser?.last_name,
    selectedUser?.nickname,
    memberOptions,
  ]);

  const autocompleteOptions = useMemo(() => {
    const options = memberOptions ? [...memberOptions] : [];
    if (
      selectedMemberOption &&
      !options.some((o) => o.MemID === selectedMemberOption.MemID)
    ) {
      options.push(selectedMemberOption);
    }
    return options;
  }, [memberOptions, selectedMemberOption]);

  // Debounce search query update
  const updateSearch = useMemo(
    () =>
      debounce((val: string) => {
        setDebouncedSearch(val);
      }, 400),
    [],
  );

  useEffect(() => {
    updateSearch(searchQuery);
    return () => updateSearch.cancel();
  }, [searchQuery, updateSearch]);

  // Fetch users query using tRPC — filtered by logged-in user's StructID
  const {
    data: users,
    isLoading,
    refetch,
  } = trpc.user.list.useQuery(
    {
      search: debouncedSearch || undefined,
      roles: roleFilter.length > 0 ? roleFilter : undefined,
      enabled:
        statusFilter === "active"
          ? true
          : statusFilter === "inactive"
            ? false
            : undefined,
      structId: user?.StructID ?? undefined,
      limit: 200, // Fetch up to 200 users for interactive client-side sorting/paging
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  // Update user mutation using tRPC
  const updateMutation = trpc.user.update.useMutation({
    onSuccess: () => {
      enqueueSnackbar("User configuration updated successfully!", {
        variant: "success",
      });
      setIsDialogOpen(false);
      refetch();
    },
    onError: (error) => {
      enqueueSnackbar(error.message || "Failed to update user configuration.", {
        variant: "error",
      });
    },
  });

  // Delete user mutation using tRPC
  const deleteMutation = trpc.user.delete.useMutation({
    onSuccess: () => {
      enqueueSnackbar("User deleted successfully!", { variant: "success" });
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
      refetch();
    },
    onError: (error) => {
      enqueueSnackbar(error.message || "Failed to delete user.", {
        variant: "error",
      });
    },
  });

  // Impersonate user mutation using tRPC
  const impersonateMutation = trpc.auth.impersonateUser.useMutation({
    onSuccess: (response) => {
      enqueueSnackbar(
        `Successfully logged in as ${response.data.user.first_name || ""} ${response.data.user.last_name || ""}!`,
        { variant: "success" },
      );
      setIsImpersonateDialogOpen(false);

      // Perform frontend login update
      login(response.data.user as any, response.data.token);

      // Redirect to profile page
      navigate("/profile");
    },
    onError: (error) => {
      enqueueSnackbar(error.message || "Failed to impersonate user.", {
        variant: "error",
      });
    },
  });

  const handleOpenImpersonate = (user: any) => {
    setUserToImpersonate(user);
    setIsImpersonateDialogOpen(true);
  };

  const handleConfirmImpersonate = () => {
    if (!userToImpersonate) return;
    impersonateMutation.mutate({ userId: userToImpersonate.id });
  };

  const handleOpenDelete = (user: any) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!userToDelete) return;
    deleteMutation.mutate(userToDelete.id);
  };

  // Structure tree data for @mui/x-tree-view RichTreeView
  const [structAnchorEl, setStructAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [wflStructAnchorEl, setWflStructAnchorEl] =
    useState<HTMLElement | null>(null);
  const { data: rawTree } = trpc.structure.getStructureTree.useQuery({
    rootId: 301,
  });

  // Convert backend tree (value/label/children) to RichTreeView format (id/label/children)
  const treeItems = useMemo(() => {
    if (!rawTree) return [];
    const nodesList = Array.isArray(rawTree) ? rawTree : ((rawTree as any)?.nodes || []);
    const convert = (nodes: any[]): any[] =>
      nodes.map((n) => ({
        id: String(n.value),
        label: n.label,
        children: n.children?.length > 0 ? convert(n.children) : undefined,
      }));
    return convert(nodesList);
  }, [rawTree]);

  // Collect all node IDs for auto-expanding the tree
  const allTreeIds = useMemo(() => {
    const collect = (nodes: any[]): string[] =>
      nodes.flatMap((n) => [n.id, ...(n.children ? collect(n.children) : [])]);
    return collect(treeItems);
  }, [treeItems]);

  // Find label by numeric StructID
  const findLabel = (nodes: any[], value: number): string => {
    for (const n of nodes) {
      if (Number(n.id) === value) return n.label;
      if (n.children) {
        const found = findLabel(n.children, value);
        if (found) return found;
      }
    }
    return "";
  };

  // Open Edit Dialog
  const handleOpenEdit = (user: any) => {
    // Standardize roles and apps to string arrays safely
    let userRoles: string[] = [];
    if (Array.isArray(user.roles)) {
      userRoles = user.roles;
    } else if (user.roles && typeof user.roles === "object") {
      userRoles = Object.keys(user.roles);
    }

    let userApps: string[] = [];
    if (Array.isArray(user.apps)) {
      userApps = user.apps;
    } else if (user.apps && typeof user.apps === "object") {
      userApps = Object.keys(user.apps);
    }

    setSelectedUser({
      id: user.id,
      email: user.email || "",
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      enabled: user.enabled ?? true,
      roles: userRoles,
      apps: userApps,
      ChurchID: user.ChurchID ? String(user.ChurchID) : "",
      StructID: user.StructID ? String(user.StructID) : "",
      WFLStructID: user.WFLStructID ? String(user.WFLStructID) : "",
      TeacherID: user.TeacherID ? String(user.TeacherID) : "",
      StrucName:
        user.acts_structure_user_StructIDToacts_structure?.StrucName || "",
      WFLStrucName:
        user.acts_structure_user_WFLStructIDToacts_structure?.StrucName || "",
      password: "",
      nickname: user.acts_member?.Nickname || (user.MemID ? "" : "not link"),
      MemID: user.MemID ? String(user.MemID) : "",
    });
    setIsDialogOpen(true);
  };

  // Submit edit form
  const handleSave = () => {
    if (!selectedUser) return;

    const payload = {
      id: selectedUser.id,
      email: selectedUser.email,
      first_name: selectedUser.first_name || undefined,
      last_name: selectedUser.last_name || undefined,
      enabled: selectedUser.enabled,
      roles: selectedUser.roles,
      apps: selectedUser.apps,
      ChurchID: selectedUser.ChurchID ? Number(selectedUser.ChurchID) : null,
      StructID: selectedUser.StructID ? Number(selectedUser.StructID) : null,
      WFLStructID: selectedUser.WFLStructID
        ? Number(selectedUser.WFLStructID)
        : null,
      TeacherID: selectedUser.TeacherID ? Number(selectedUser.TeacherID) : null,
      password: selectedUser.password || undefined,
      MemID: selectedUser.MemID ? Number(selectedUser.MemID) : null,
    };

    updateMutation.mutate(payload as any);
  };

  // Colors for roles chips
  const getRoleChipColor = (role: string) => {
    switch (role) {
      case Roles.ROLE_SUPERADMIN:
      case Roles.ROLE_ADMIN:
        return {
          bg: "rgba(147, 51, 234, 0.1)",
          text: "#7c3aed",
          border: "rgba(147, 51, 234, 0.25)",
        }; // purple
      case Roles.ROLE_EVENT_ADMIN:
        return {
          bg: "rgba(79, 70, 229, 0.1)",
          text: "#4338ca",
          border: "rgba(79, 70, 229, 0.25)",
        }; // indigo
      case Roles.ROLE_EVENT_ACCOUNT:
        return {
          bg: "rgba(16, 185, 129, 0.1)",
          text: "#059669",
          border: "rgba(16, 185, 129, 0.25)",
        }; // emerald
      case Roles.ROLE_EVENT_VIEW:
        return {
          bg: "rgba(2, 132, 199, 0.1)",
          text: "#0284c7",
          border: "rgba(2, 132, 199, 0.25)",
        }; // sky
      case Roles.ROLE_ADMIN_CHURCH:
        return {
          bg: "rgba(6, 148, 162, 0.1)",
          text: "#0e7490",
          border: "rgba(6, 148, 162, 0.25)",
        }; // cyan
      case Roles.ROLE_ADMIN_WFL:
      case Roles.ROLE_WFL_ADMIN:
      case Roles.ROLE_ADMIN_SC:
        return {
          bg: "rgba(234, 88, 12, 0.1)",
          text: "#c2410c",
          border: "rgba(234, 88, 12, 0.25)",
        }; // orange
      case Roles.ROLE_WFL_TEACHER:
      case Roles.ROLE_WFL_TEAM:
      case Roles.ROLE_WFL_MONITOR:
        return {
          bg: "rgba(217, 119, 6, 0.1)",
          text: "#b45309",
          border: "rgba(217, 119, 6, 0.25)",
        }; // amber
      case Roles.ROLE_GROUP_LEADER:
      case Roles.ROLE_ZONE_LEADER:
      case Roles.ROLE_HIM_LEADER:
      case Roles.ROLE_CHURCH_MONITOR:
        return {
          bg: "rgba(13, 148, 136, 0.1)",
          text: "#0f766e",
          border: "rgba(13, 148, 136, 0.25)",
        }; // teal
      case Roles.ROLE_ANALYTICS_CHURCH_HEALTH:
      case Roles.ROLE_ANALYTICS_CHART_DATA:
        return {
          bg: "rgba(236, 72, 153, 0.1)",
          text: "#db2777",
          border: "rgba(236, 72, 153, 0.25)",
        }; // pink
      default:
        return {
          bg: "rgba(71, 85, 105, 0.08)",
          text: "#475569",
          border: "rgba(71, 85, 105, 0.2)",
        }; // slate
    }
  };

  // Columns definition for DataGrid
  const columns: GridColDef[] = [
    // { field: 'id', headerName: 'ID', width: 60 },
    {
      field: "name",
      headerName: "Name",
      width: 180,
      valueGetter: (_, row) =>
        `${row.first_name || ""} ${row.last_name || ""}`.trim() || "-",
    },
    {
      field: "nickname",
      headerName: "Nickname",
      width: 130,
      valueGetter: (_, row) =>
        row.MemID ? row.acts_member?.Nickname || "" : "not link",
      renderCell: (params) => {
        const hasLink = !!params.row.MemID;
        const name = params.value;
        if (!hasLink) {
          return (
            <span
              style={{
                color: "#94a3b8",
                fontStyle: "italic",
                fontSize: "0.85rem",
              }}
            >
              not link
            </span>
          );
        }
        if (!name) {
          return (
            <span style={{ color: "#94a3b8", fontSize: "0.8rem" }}>—</span>
          );
        }
        return (
          <Typography
            variant="body2"
            sx={{ color: "#1e293b", fontWeight: 500 }}
          >
            {name}
          </Typography>
        );
      },
    },
    { field: "email", headerName: "Email", width: 230 },
    {
      field: "StrucName",
      headerName: "Structure",
      width: 170,
      valueGetter: (_, row) =>
        row.acts_structure_user_StructIDToacts_structure?.StrucName || "",
      renderCell: (params) => {
        const name = params.value;
        if (!name)
          return (
            <span style={{ color: "#94a3b8", fontSize: "0.8rem" }}>—</span>
          );
        return (
          <Chip
            label={name}
            size="small"
            sx={{
              bgcolor: "rgba(16, 185, 129, 0.08)",
              color: "#059669",
              border: "1px solid rgba(16, 185, 129, 0.25)",
              fontSize: "0.75rem",
              fontWeight: 600,
            }}
          />
        );
      },
    },
    {
      field: "WFLStrucName",
      headerName: "WFL Structure",
      width: 170,
      valueGetter: (_, row) =>
        row.acts_structure_user_WFLStructIDToacts_structure?.StrucName || "",
      renderCell: (params) => {
        const name = params.value;
        if (!name)
          return (
            <span style={{ color: "#94a3b8", fontSize: "0.8rem" }}>—</span>
          );
        return (
          <Chip
            label={name}
            size="small"
            sx={{
              bgcolor: "rgba(99, 102, 241, 0.08)",
              color: "#4f46e5",
              border: "1px solid rgba(99, 102, 241, 0.25)",
              fontSize: "0.75rem",
              fontWeight: 600,
            }}
          />
        );
      },
    },
    {
      field: "roles",
      headerName: "Roles",
      minWidth: 280,
      flex: 1.2,
      renderCell: (params) => {
        let roles: string[] = [];
        if (Array.isArray(params.value)) {
          roles = params.value;
        } else if (params.value && typeof params.value === "object") {
          roles = Object.keys(params.value);
        }

        if (roles.length === 0)
          return <span className="text-slate-500 text-xs">None</span>;

        return (
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              flexWrap: "wrap",
              alignItems: "center",
              py: 0.75,
              width: "100%",
            }}
          >
            {roles.map((role) => {
              const colors = getRoleChipColor(role);
              return (
                <Chip
                  key={role}
                  label={role.replace("ROLE_", "")}
                  size="small"
                  sx={{
                    bgcolor: colors.bg,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                  }}
                />
              );
            })}
          </Box>
        );
      },
    },
    // {
    //   field: "apps",
    //   headerName: "Apps Access",
    //   width: 220,
    //   renderCell: (params) => {
    //     let apps: string[] = [];
    //     if (Array.isArray(params.value)) {
    //       apps = params.value;
    //     } else if (params.value && typeof params.value === "object") {
    //       apps = Object.keys(params.value);
    //     }

    //     if (apps.length === 0)
    //       return <span className="text-slate-500 text-xs">None</span>;

    //     return (
    //       <Box
    //         sx={{
    //           display: "flex",
    //           gap: 0.5,
    //           flexWrap: "wrap",
    //           alignItems: "center",
    //           height: "100%",
    //         }}
    //       >
    //         {apps.map((app) => (
    //           <Chip
    //             key={app}
    //             label={app}
    //             size="small"
    //             variant="outlined"
    //             sx={{
    //               color: "#0284c7",
    //               borderColor: "rgba(2, 132, 199, 0.35)",
    //               bgcolor: "rgba(2, 132, 199, 0.06)",
    //               fontSize: "0.7rem",
    //               height: 20,
    //             }}
    //           />
    //         ))}
    //       </Box>
    //     );
    //   },
    // },
    {
      field: "enabled",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        const isEnabled = params.value ?? true;
        return isEnabled ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: "#16a34a",
              height: "100%",
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 16 }} />
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontSize: "0.85rem" }}
            >
              Active
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: "#dc2626",
              height: "100%",
            }}
          >
            <CancelIcon sx={{ fontSize: 16 }} />
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontSize: "0.85rem" }}
            >
              Inactive
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: user?.email === "superhuiman@gmail.com" ? 160 : 120,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{ display: "flex", alignItems: "center", height: "100%", gap: 1 }}
        >
          <IconButton
            size="small"
            onClick={() => handleOpenEdit(params.row)}
            sx={{
              color: "#3b82f6",
              "&:hover": { bgcolor: "rgba(59, 130, 246, 0.08)" },
            }}
          >
            <EditIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleOpenDelete(params.row)}
            sx={{
              color: "#ef4444",
              "&:hover": { bgcolor: "rgba(239, 68, 68, 0.08)" },
            }}
          >
            <DeleteIcon sx={{ fontSize: 20 }} />
          </IconButton>
          {user?.email === "superhuiman@gmail.com" && (
            <IconButton
              size="small"
              onClick={() => handleOpenImpersonate(params.row)}
              sx={{
                color: "#10b981",
                "&:hover": { bgcolor: "rgba(16, 185, 129, 0.08)" },
              }}
              title="Login as user"
            >
              <FlutterDashIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 p-4 md:p-8 font-sans">
      <div className="_max-w-7xl mx-auto space-y-6">
        {/* Header Breadcrumbs / Title */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="space-y-1">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/profile")}
              sx={{
                color: "#64748b",
                mb: 1,
                textTransform: "none",
                "&:hover": { color: "#1e293b", bgcolor: "rgba(0,0,0,0.04)" },
              }}
            >
              Back to Profile
            </Button>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-800">
              User Management
            </h1>
            <p className="text-slate-500 text-sm">
              Manage accounts, toggle status, and configure app access, roles,
              and structural permissions.
            </p>
          </div>
        </div>

        {/* Search & DataGrid Container */}
        <Card
          sx={{
            bgcolor: "#dbdbdb",
            border: "1px solid #e2e8f0",
            borderRadius: "1.25rem",
            p: 3,
            boxShadow: "0 4px 24px rgba(0, 0, 0, 0.06)",
          }}
        >
          {/* Search Controls */}
          <Box
            sx={{
              mb: 3,
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <TextField
              placeholder="Search by name or email..."
              variant="outlined"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery("")}
                        edge="end"
                        aria-label="clear search"
                        sx={{
                          color: "#94a3b8",
                          "&:hover": {
                            color: "#475569",
                            bgcolor: "rgba(0,0,0,0.04)",
                          },
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                  sx: {
                    color: "#1e293b",
                    bgcolor: "#f8fafc",
                    borderRadius: "0.75rem",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e2e8f0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#3b82f6",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#3b82f6",
                    },
                  },
                },
              }}
            />

            <Autocomplete
              multiple
              options={Object.values(Roles)}
              value={roleFilter}
              onChange={(_, newValue) => setRoleFilter(newValue)}
              renderTags={(value: readonly string[], getTagProps) =>
                value.map((option: string, index: number) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      variant="outlined"
                      label={option}
                      size="small"
                      key={key}
                      {...tagProps}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Filter by role..."
                  variant="outlined"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      sx: {
                        color: "#1e293b",
                        bgcolor: "#f8fafc",
                        borderRadius: "0.75rem",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#e2e8f0",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#3b82f6",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#3b82f6",
                        },
                      },
                    },
                  }}
                />
              )}
              sx={{ minWidth: { xs: "100%", sm: 250 } }}
            />

            <FormControl
              variant="outlined"
              sx={{ minWidth: { xs: "100%", sm: 150 } }}
            >
              <Select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(
                    e.target.value as "all" | "active" | "inactive",
                  )
                }
                displayEmpty
                sx={{
                  color: "#1e293b",
                  bgcolor: "#f8fafc",
                  borderRadius: "0.75rem",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e2e8f0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#3b82f6",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#3b82f6",
                  },
                }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* User Table Grid */}
          <Box sx={{ height: 600, width: "100%" }}>
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress sx={{ color: "#3b82f6" }} />
              </Box>
            ) : (
              <DataGrid
                rows={users || []}
                columns={columns}
                getRowHeight={() => "auto"}
                getEstimatedRowHeight={() => 52}
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 25 },
                  },
                }}
                pageSizeOptions={[10, 25, 50]}
                disableRowSelectionOnClick
                sx={{
                  color: "#1e293b",
                  border: "none",
                  fontSize: "0.9rem",
                  "& .MuiDataGrid-row": {
                    borderBottomColor: "#f1f5f9",
                    "&:hover": {
                      backgroundColor: "#f8fafc",
                    },
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottomColor: "#f1f5f9",
                    display: "flex",
                    alignItems: "center",
                    py: 1,
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                    color: "#64748b",
                    fontWeight: 700,
                  },
                  "& .MuiDataGrid-columnHeader": {
                    backgroundColor: "#f8fafc",
                  },
                  "& .MuiDataGrid-footerContainer": {
                    borderTop: "1px solid #e2e8f0",
                    backgroundColor: "#f8fafc",
                  },
                  "& .MuiTablePagination-root": {
                    color: "#64748b",
                  },
                  "& .MuiTablePagination-selectIcon": {
                    color: "#64748b",
                  },
                  "& .MuiDataGrid-scrollbar": {
                    bgcolor: "transparent",
                  },
                }}
              />
            )}
          </Box>
        </Card>
      </div>

      {/* Edit User Modal Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#ffffff",
            color: "#1e293b",
            borderRadius: "1.25rem",
            border: "1px solid #e2e8f0",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.12)",
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            color: "#0f172a",
            borderBottom: "1px solid #f1f5f9",
            pb: 2,
          }}
        >
          Edit User Profile
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <div className="mt-1">
            {selectedUser && (
              <Grid container spacing={2}>
                {/* Basic Fields */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="First Name"
                    fullWidth
                    value={selectedUser.first_name}
                    onChange={(e) =>
                      setSelectedUser({
                        ...selectedUser,
                        first_name: e.target.value,
                      })
                    }
                    slotProps={{
                      inputLabel: { style: { color: "#64748b" } },
                      input: {
                        sx: {
                          color: "#1e293b",
                          bgcolor: "#f8fafc",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e2e8f0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#3b82f6",
                          },
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Last Name"
                    fullWidth
                    value={selectedUser.last_name}
                    onChange={(e) =>
                      setSelectedUser({
                        ...selectedUser,
                        last_name: e.target.value,
                      })
                    }
                    slotProps={{
                      inputLabel: { style: { color: "#64748b" } },
                      input: {
                        sx: {
                          color: "#1e293b",
                          bgcolor: "#f8fafc",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e2e8f0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#3b82f6",
                          },
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    options={autocompleteOptions}
                    filterOptions={(x) => x}
                    isOptionEqualToValue={(option, value) =>
                      option.MemID === value?.MemID
                    }
                    getOptionLabel={(option) =>
                      `${option.Name || ""} ${option.Surname || ""} (${option.Nickname || "No Nickname"})]`
                    }
                    value={selectedMemberOption}
                    onChange={(_, newValue) => {
                      setSelectedUser({
                        ...selectedUser,
                        MemID: newValue ? String(newValue.MemID) : "",
                        nickname: newValue
                          ? newValue.Nickname || ""
                          : "not link",
                      });
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                      if (reason === "input") {
                        setMemberSearchQuery(newInputValue);
                      } else if (reason === "clear") {
                        setMemberSearchQuery("");
                      }
                    }}
                    loading={isMemberLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Linked Member (MemID)"
                        placeholder="Search member by name, surname, or nickname..."
                        slotProps={{
                          inputLabel: { style: { color: "#64748b" } },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            color: "#1e293b",
                            bgcolor: "#f8fafc",
                            "& fieldset": { borderColor: "#e2e8f0" },
                            "&:hover fieldset": { borderColor: "#3b82f6" },
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Nickname"
                    fullWidth
                    value={selectedUser.nickname || ""}
                    slotProps={{
                      inputLabel: { style: { color: "#64748b" } },
                      input: {
                        readOnly: true,
                        sx: {
                          color: selectedUser.MemID ? "#1e293b" : "#94a3b8",
                          fontStyle: selectedUser.MemID ? "normal" : "italic",
                          bgcolor: "#f1f5f9",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e2e8f0",
                          },
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Email"
                    fullWidth
                    value={selectedUser.email}
                    onChange={(e) =>
                      setSelectedUser({
                        ...selectedUser,
                        email: e.target.value,
                      })
                    }
                    slotProps={{
                      inputLabel: { style: { color: "#64748b" } },
                      input: {
                        sx: {
                          color: "#1e293b",
                          bgcolor: "#f8fafc",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e2e8f0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#3b82f6",
                          },
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="New Password (leave blank to keep current password)"
                    type="password"
                    fullWidth
                    value={selectedUser.password || ""}
                    onChange={(e) =>
                      setSelectedUser({
                        ...selectedUser,
                        password: e.target.value,
                      })
                    }
                    slotProps={{
                      inputLabel: { style: { color: "#64748b" } },
                      input: {
                        sx: {
                          color: "#1e293b",
                          bgcolor: "#f8fafc",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e2e8f0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#3b82f6",
                          },
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Status toggle */}
                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedUser.enabled}
                        onChange={(e) =>
                          setSelectedUser({
                            ...selectedUser,
                            enabled: e.target.checked,
                          })
                        }
                        color="primary"
                      />
                    }
                    label={
                      <Typography sx={{ color: "#475569", fontWeight: 600 }}>
                        Account Enabled (
                        {selectedUser.enabled ? "Active" : "Inactive"})
                      </Typography>
                    }
                  />
                </Grid>

                {/* Roles Autocomplete */}
                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    multiple
                    disabled={!canManageRoles}
                    options={roleOptions}
                    value={selectedUser.roles}
                    onChange={(_, newValue) => {
                      if (!canManageRoles) return;
                      if (!isSuperOrAdmin) {
                        // Keep existing ADMIN roles that were on the user before, while allowing WFL admins to assign WFL admin roles
                        const existingAdminRoles = (
                          selectedUser.roles || []
                        ).filter((r) => r.toUpperCase().includes("ADMIN"));
                        const filtered = newValue.filter(
                          (r) =>
                            !r.toUpperCase().includes("ADMIN") ||
                            existingAdminRoles.includes(r) ||
                            (canAssignWflAdmin &&
                              (r === Roles.ROLE_ADMIN_WFL ||
                                r === Roles.ROLE_WFL_ADMIN)),
                        );
                        setSelectedUser({ ...selectedUser, roles: filtered });
                      } else {
                        setSelectedUser({ ...selectedUser, roles: newValue });
                      }
                    }}
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        const isAdminTag =
                          option.toUpperCase().includes("ADMIN");
                        const isWflAdminTag =
                          option === Roles.ROLE_ADMIN_WFL ||
                          option === Roles.ROLE_WFL_ADMIN;
                        const canDeleteTag =
                          canManageRoles &&
                          (isSuperOrAdmin ||
                            !isAdminTag ||
                            (canAssignWflAdmin && isWflAdminTag));
                        return (
                          <Chip
                            key={key}
                            label={option.replace("ROLE_", "")}
                            size="small"
                            {...tagProps}
                            onDelete={
                              canDeleteTag ? tagProps.onDelete : undefined
                            }
                            sx={{
                              bgcolor: "rgba(56, 189, 248, 0.15)",
                              color: "#38bdf8",
                              border: "1px solid rgba(56, 189, 248, 0.3)",
                            }}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Roles"
                        helperText={
                          !canManageRoles
                            ? "Only users with an ADMIN role can edit and add roles."
                            : !isSuperOrAdmin && !canAssignWflAdmin
                              ? "Only ROLE_ADMIN and ROLE_SUPERADMIN can add ADMIN roles."
                              : undefined
                        }
                        slotProps={{
                          inputLabel: { style: { color: "#64748b" } },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            color: "#1e293b",
                            bgcolor: "#f8fafc",
                            "& fieldset": { borderColor: "#e2e8f0" },
                            "&:hover fieldset": { borderColor: "#3b82f6" },
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Apps Autocomplete */}
                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    multiple
                    options={AVAILABLE_APPS}
                    value={selectedUser.apps}
                    onChange={(_, newValue) =>
                      setSelectedUser({ ...selectedUser, apps: newValue })
                    }
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            label={option}
                            size="small"
                            {...tagProps}
                            sx={{
                              bgcolor: "rgba(20, 184, 166, 0.15)",
                              color: "#14b8a6",
                              border: "1px solid rgba(20, 184, 166, 0.3)",
                            }}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Applications Access"
                        slotProps={{
                          inputLabel: { style: { color: "#64748b" } },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            color: "#1e293b",
                            bgcolor: "#f8fafc",
                            "& fieldset": { borderColor: "#e2e8f0" },
                            "&:hover fieldset": { borderColor: "#3b82f6" },
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Structure and Member IDs */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Church ID"
                    type="number"
                    fullWidth
                    value={selectedUser.ChurchID}
                    onChange={(e) =>
                      setSelectedUser({
                        ...selectedUser,
                        ChurchID: e.target.value,
                      })
                    }
                    slotProps={{
                      inputLabel: { style: { color: "#64748b" } },
                      input: {
                        sx: {
                          color: "#1e293b",
                          bgcolor: "#f8fafc",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e2e8f0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#3b82f6",
                          },
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Structure"
                    fullWidth
                    value={
                      selectedUser.StructID
                        ? findLabel(treeItems, Number(selectedUser.StructID)) ||
                          selectedUser.StrucName ||
                          `ID: ${selectedUser.StructID}`
                        : ""
                    }
                    placeholder="Click to select structure..."
                    onClick={(e) => setStructAnchorEl(e.currentTarget)}
                    slotProps={{
                      inputLabel: { style: { color: "#64748b" } },
                      input: {
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <AccountTreeIcon
                              sx={{ color: "#94a3b8", fontSize: 20 }}
                            />
                          </InputAdornment>
                        ),
                        sx: {
                          cursor: "pointer",
                          color: "#1e293b",
                          bgcolor: "#f8fafc",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e2e8f0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#3b82f6",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#3b82f6",
                          },
                        },
                      },
                    }}
                  />
                  <Popover
                    open={Boolean(structAnchorEl)}
                    anchorEl={structAnchorEl}
                    onClose={() => setStructAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    PaperProps={{
                      sx: {
                        mt: 0.5,
                        borderRadius: "0.75rem",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                        border: "1px solid #e2e8f0",
                      },
                    }}
                  >
                    <Paper
                      sx={{
                        p: 1,
                        width: 320,
                        maxHeight: 400,
                        overflow: "auto",
                      }}
                    >
                      <RichTreeView
                        items={treeItems}
                        defaultExpandedItems={allTreeIds}
                        selectedItems={
                          selectedUser.StructID
                            ? String(selectedUser.StructID)
                            : ""
                        }
                        onSelectedItemsChange={(_e, itemId) => {
                          const label = itemId
                            ? findLabel(treeItems, Number(itemId))
                            : "";
                          setSelectedUser({
                            ...selectedUser,
                            StructID: itemId ?? "",
                            StrucName: label || undefined,
                          });
                          setStructAnchorEl(null);
                        }}
                        sx={{
                          "& .MuiTreeItem-label": {
                            fontSize: "0.875rem",
                            py: 0.25,
                          },
                          "& .Mui-selected": {
                            bgcolor: "rgba(59,130,246,0.1) !important",
                            color: "#3b82f6",
                            fontWeight: 600,
                          },
                        }}
                      />
                    </Paper>
                  </Popover>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="WFL Structure"
                    fullWidth
                    value={
                      selectedUser.WFLStructID
                        ? findLabel(
                            treeItems,
                            Number(selectedUser.WFLStructID),
                          ) ||
                          selectedUser.WFLStrucName ||
                          `ID: ${selectedUser.WFLStructID}`
                        : ""
                    }
                    placeholder="Click to select structure..."
                    onClick={(e) => setWflStructAnchorEl(e.currentTarget)}
                    slotProps={{
                      inputLabel: { style: { color: "#64748b" } },
                      input: {
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <AccountTreeIcon
                              sx={{ color: "#94a3b8", fontSize: 20 }}
                            />
                          </InputAdornment>
                        ),
                        sx: {
                          cursor: "pointer",
                          color: "#1e293b",
                          bgcolor: "#f8fafc",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e2e8f0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#3b82f6",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#3b82f6",
                          },
                        },
                      },
                    }}
                  />
                  <Popover
                    open={Boolean(wflStructAnchorEl)}
                    anchorEl={wflStructAnchorEl}
                    onClose={() => setWflStructAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    PaperProps={{
                      sx: {
                        mt: 0.5,
                        borderRadius: "0.75rem",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                        border: "1px solid #e2e8f0",
                      },
                    }}
                  >
                    <Paper
                      sx={{
                        p: 1,
                        width: 320,
                        maxHeight: 400,
                        overflow: "auto",
                      }}
                    >
                      <RichTreeView
                        items={treeItems}
                        defaultExpandedItems={allTreeIds}
                        selectedItems={
                          selectedUser.WFLStructID
                            ? String(selectedUser.WFLStructID)
                            : ""
                        }
                        onSelectedItemsChange={(_e, itemId) => {
                          const label = itemId
                            ? findLabel(treeItems, Number(itemId))
                            : "";
                          setSelectedUser({
                            ...selectedUser,
                            WFLStructID: itemId ?? "",
                            WFLStrucName: label || undefined,
                          });
                          setWflStructAnchorEl(null);
                        }}
                        sx={{
                          "& .MuiTreeItem-label": {
                            fontSize: "0.875rem",
                            py: 0.25,
                          },
                          "& .Mui-selected": {
                            bgcolor: "rgba(59,130,246,0.1) !important",
                            color: "#3b82f6",
                            fontWeight: 600,
                          },
                        }}
                      />
                    </Paper>
                  </Popover>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Teacher ID (WFL Teacher)"
                    type="number"
                    fullWidth
                    value={selectedUser.TeacherID}
                    onChange={(e) =>
                      setSelectedUser({
                        ...selectedUser,
                        TeacherID: e.target.value,
                      })
                    }
                    slotProps={{
                      inputLabel: { style: { color: "#64748b" } },
                      input: {
                        sx: {
                          color: "#1e293b",
                          bgcolor: "#f8fafc",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e2e8f0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#3b82f6",
                          },
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </div>
        </DialogContent>

        <DialogActions
          sx={{ borderTop: "1px solid #f1f5f9", pt: 2, px: 3, pb: 2 }}
        >
          <Button
            onClick={() => setIsDialogOpen(false)}
            sx={{
              color: "#64748b",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { color: "#1e293b", bgcolor: "#f1f5f9" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            variant="contained"
            sx={{
              bgcolor: "#3b82f6",
              color: "white",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "0.5rem",
              boxShadow: "0 4px 12px rgba(59,130,246,0.3)",
              "&:hover": {
                bgcolor: "#2563eb",
                boxShadow: "0 6px 16px rgba(59,130,246,0.4)",
              },
              "&.Mui-disabled": {
                bgcolor: "rgba(59, 130, 246, 0.3)",
                color: "rgba(255,255,255,0.6)",
              },
            }}
          >
            {updateMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "1rem",
            padding: 1,
            maxWidth: 400,
            boxShadow:
              "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#1e293b", pb: 1 }}>
          Delete User
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: "#475569", mb: 1 }}>
            Are you sure you want to delete user{" "}
            <strong>
              {userToDelete
                ? `${userToDelete.first_name || ""} ${userToDelete.last_name || ""}`.trim() ||
                  userToDelete.email
                : ""}
            </strong>
            ?
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#ef4444", fontWeight: 500 }}
          >
            This action is permanent and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setIsDeleteDialogOpen(false)}
            sx={{
              color: "#64748b",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#f1f5f9" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={deleteMutation.isPending}
            variant="contained"
            sx={{
              bgcolor: "#ef4444",
              color: "white",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "0.5rem",
              boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
              "&:hover": {
                bgcolor: "#dc2626",
                boxShadow: "0 6px 16px rgba(239, 68, 68, 0.4)",
              },
              "&.Mui-disabled": {
                bgcolor: "rgba(239, 68, 68, 0.3)",
                color: "rgba(255,255,255,0.6)",
              },
            }}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Impersonation Confirmation Dialog */}
      <Dialog
        open={isImpersonateDialogOpen}
        onClose={() => setIsImpersonateDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "1rem",
            padding: 1,
            maxWidth: 400,
            boxShadow:
              "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#1e293b", pb: 1 }}>
          Login As User
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: "#475569", mb: 1 }}>
            Are you sure you want to login as{" "}
            <strong>
              {userToImpersonate
                ? `${userToImpersonate.first_name || ""} ${userToImpersonate.last_name || ""}`.trim() ||
                  userToImpersonate.email
                : ""}
            </strong>
            ?
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#0f766e", fontWeight: 500 }}
          >
            This will bypass the login process and authenticate you as this
            user.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setIsImpersonateDialogOpen(false)}
            sx={{
              color: "#64748b",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#f1f5f9" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmImpersonate}
            disabled={impersonateMutation.isPending}
            variant="contained"
            sx={{
              bgcolor: "#10b981",
              color: "white",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "0.5rem",
              boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
              "&:hover": {
                bgcolor: "#059669",
                boxShadow: "0 6px 16px rgba(16, 185, 129, 0.4)",
              },
              "&.Mui-disabled": {
                bgcolor: "rgba(16, 185, 129, 0.3)",
                color: "rgba(255,255,255,0.6)",
              },
            }}
          >
            {impersonateMutation.isPending ? "Logging in..." : "Login"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
